import React, { useState, useEffect } from "react";
import {
  Package, Users, Map, ClipboardList, TestTube2, GitGraph,
} from "lucide-react";
import { usePlm } from "../../hooks/usePlm";
import useStore from "../../utils/store";
import ProductsTab from "./ProductsTab";
import PersonasTab from "./PersonasTab";
import JourneysTab from "./JourneysTab";
import RequirementsTab from "./RequirementsTab";
import TestCasesTab from "./TestCasesTab";
import TraceabilityTab from "./TraceabilityTab";
import "./PlmStyles.css";

const TABS = [
  { key: "personas",      label: "Personas",       icon: Users },
  { key: "journeys",      label: "Journeys",       icon: Map },
  { key: "requirements",  label: "Requirements",   icon: ClipboardList },
  { key: "testcases",     label: "Test Cases",     icon: TestTube2 },
  { key: "traceability",  label: "Traceability",   icon: GitGraph },
];

// PLM data belongs to a workspace: remount (reset selection + refetch) when it changes.
const PlmPage = ({ auth }) => {
  const workspaceId = useStore((state) => state.workspaceId);
  if (!workspaceId) return null;
  return <PlmWorkspaceView key={workspaceId} auth={auth} />;
};

const PlmWorkspaceView = ({ auth }) => {
  const token = auth.user?.id_token;
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [activeTab, setActiveTab] = useState("personas");

  const plm = usePlm({ token, productId: selectedProductId });

  useEffect(() => {
    if (!selectedProductId && plm.products.length > 0) {
      setSelectedProductId(plm.products[0].id);
    }
  }, [plm.products, selectedProductId]);

  const selectedProduct = plm.products.find(p => p.id === selectedProductId);

  const renderTab = () => {
    if (!selectedProductId) {
      return (
        <div className="plm-empty">
          <div className="plm-empty-icon"><Package size={28} /></div>
          <div className="plm-empty-title">No product selected</div>
          <div className="plm-empty-desc">Select or create a product to get started.</div>
        </div>
      );
    }
    switch (activeTab) {
      case "personas":      return <PersonasTab token={token} plm={plm} />;
      case "journeys":      return <JourneysTab token={token} plm={plm} />;
      case "requirements":  return <RequirementsTab token={token} plm={plm} />;
      case "testcases":     return <TestCasesTab token={token} plm={plm} />;
      case "traceability":  return <TraceabilityTab token={token} plm={plm} selectedProductId={selectedProductId} />;
      default:              return null;
    }
  };

  return (
    <div className="plm-page">
      {/* Header */}
      <div className="plm-header">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <Package size={20} />
            <select
              className="plm-product-selector"
              value={selectedProductId || ""}
              onChange={(e) => setSelectedProductId(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">-- Select Product --</option>
              {plm.products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <ProductsTab token={token} plm={plm} inline selectedProductId={selectedProductId} />
        </div>

        {selectedProductId && (
          <div className="plm-tabs">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                className={`plm-tab ${activeTab === key ? "active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error banner */}
      {plm.error && (
        <div style={{ padding: "12px 24px" }}>
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
            padding: "10px 16px", color: "#dc2626", fontSize: "0.85rem",
          }}>
            {plm.error}
          </div>
        </div>
      )}

      {/* Content */}
      <div className={`plm-content ${activeTab !== "journeys" ? "constrained" : ""}`}>
        {plm.loading ? (
          <div className="plm-loading">
            <div className="plm-loading-dots">
              <span /><span /><span />
            </div>
          </div>
        ) : (
          renderTab()
        )}
      </div>

      {/* Mobile bottom tabs */}
      {selectedProductId && (
        <div className="plm-mobile-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className={`plm-mobile-tab ${activeTab === key ? "active" : ""}`}
              onClick={() => setActiveTab(key)}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlmPage;
